import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes } from 'react';
import { cn } from '../../../../lib/utils.js';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Optional label shown above the input. */
  label?: string;
  /** Helper or validation text shown below the field. */
  helperText?: string;
  /** Marks the field as invalid and applies error styles. */
  error?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, label, helperText, error = false, id, disabled, ...props },
  ref
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <label className="flex w-full flex-col gap-1.5 text-body-14 text-ig-text-primary" htmlFor={inputId}>
      {label && <span className="text-label-12 font-medium uppercase tracking-wide text-ig-text-secondary">{label}</span>}
      <input
        ref={ref}
        id={inputId}
        disabled={disabled}
        aria-invalid={error || undefined}
        className={cn(
          'h-10 w-full rounded-md border bg-ig-surface-input px-3 text-body-14 text-ig-text-primary transition duration-fast ease-ig',
          'placeholder:text-ig-text-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ig-accent/60',
          error ? 'border-ig-danger' : 'border-ig-border',
          disabled && 'cursor-not-allowed opacity-60',
          className
        )}
        {...props}
      />
      {helperText && <span className={cn('text-label-12', error ? 'text-ig-danger' : 'text-ig-text-secondary')}>{helperText}</span>}
    </label>
  );
});
